import { useEffect, useMemo, useRef, useState } from "react";
import { api, formatZAR, formatInt, formatApiError } from "@/lib/api";
import { PageHeader, KpiCard } from "@/components/Kpi";
import { toast } from "sonner";
import { Upload, FileText, Send, Download, Trash2, CheckCircle2, AlertCircle, Package, Settings as SettingsIcon, ExternalLink, Copy, Ticket, Undo2, Mail } from "lucide-react";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtPeriod(p) {
    if (!p || p.length !== 6) return p;
    const y = p.slice(0, 4);
    const m = parseInt(p.slice(4), 10);
    return new Date(Number(y), m - 1, 1).toLocaleDateString("en-ZA", { month: "long", year: "numeric" });
}

export default function StockRecon() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [selected, setSelected] = useState(null);
    const [detail, setDetail] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [company, setCompany] = useState(null);
    const [companyEditing, setCompanyEditing] = useState(false);
    const [companyForm, setCompanyForm] = useState({});
    const [ticketPayload, setTicketPayload] = useState(null);
    const [markingSubmitted, setMarkingSubmitted] = useState(false);
    const [ticketRef, setTicketRef] = useState("");
    const [ticketNotes, setTicketNotes] = useState("");
    const [reminderStatus, setReminderStatus] = useState(null);
    const [sendingReminder, setSendingReminder] = useState(false);
    const fileRef = useRef(null);

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get("/stock-recon");
            setItems(r.data.items || []);
            if (!selected && r.data.items?.[0]) setSelected(r.data.items[0].period);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };
    const loadCompany = async () => {
        try {
            const r = await api.get("/stock-recon/company");
            setCompany(r.data);
            setCompanyForm(r.data);
        } catch {
            // best-effort — company details are optional
        }
    };
    const loadReminder = async () => {
        try {
            const r = await api.get("/stock-recon/reminders/status");
            setReminderStatus(r.data);
        } catch {}
    };
    const sendReminderNow = async () => {
        if (!window.confirm("Send the monthly recon reminder email to all admins right now? (Normally fires on the first Monday of the month.)")) return;
        setSendingReminder(true);
        try {
            const r = await api.post("/stock-recon/reminders/send-now");
            toast.success(`Sent to ${r.data.sent.length} admin${r.data.sent.length !== 1 ? "s" : ""} for ${r.data.period_label}`);
            await loadReminder();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSendingReminder(false); }
    };
    useEffect(() => { load(); loadCompany(); loadReminder(); }, []);
    useEffect(() => {
        if (!selected) { setDetail(null); return; }
        api.get(`/stock-recon/${selected}`).then((r) => setDetail(r.data)).catch(() => setDetail(null));
    }, [selected]);

    const onUpload = async (file) => {
        if (!file) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const r = await api.post("/stock-recon/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success(`Uploaded ${r.data.period_label} — ${r.data.claim_line_count} claim lines, ${formatZAR(r.data.totals.total)} owed`);
            setSelected(r.data.period);
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setUploading(false);
            if (fileRef.current) fileRef.current.value = "";
        }
    };

    const removeRecon = async (period) => {
        if (!window.confirm(`Delete the recon for ${fmtPeriod(period)}? The CSV and generated invoice will be lost.`)) return;
        try {
            await api.delete(`/stock-recon/${period}`);
            toast.success("Deleted");
            if (selected === period) setSelected(null);
            await load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const submit = async () => {
        if (!detail) return;
        if (!company?.takealot_claims_email) {
            toast.error("No escalation email is set — configure one in Company details, or use the support ticket workflow instead.");
            return;
        }
        if (!window.confirm(`Send the invoice ${detail.invoice_number} (${formatZAR(detail.totals.total)}) to ${company.takealot_claims_email} via Resend? Support ticket is Takealot's preferred channel — only use email if you have an escalation address.`)) return;
        setSubmitting(true);
        try {
            const r = await api.post(`/stock-recon/${selected}/submit`);
            toast.success(`Emailed — Resend id ${r.data.submission.resend_id || "—"}`);
            await load();
            const d = await api.get(`/stock-recon/${selected}`);
            setDetail(d.data);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSubmitting(false); }
    };

    const openTicket = async () => {
        if (!detail) return;
        try {
            const r = await api.get(`/stock-recon/${selected}/ticket-payload`);
            setTicketPayload(r.data);
            setTicketRef("");
            setTicketNotes("");
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const copyToClipboard = async (text, label) => {
        try {
            await navigator.clipboard.writeText(text);
            toast.success(`${label} copied to clipboard`);
        } catch {
            toast.error("Clipboard blocked — long-press the text to copy manually");
        }
    };

    const markSubmitted = async () => {
        if (!ticketRef.trim()) { toast.error("Paste the ticket reference from Takealot first"); return; }
        setMarkingSubmitted(true);
        try {
            await api.post(`/stock-recon/${selected}/mark-submitted`, {
                ticket_reference: ticketRef.trim(),
                channel: "support_ticket",
                notes: ticketNotes || null,
            });
            toast.success("Marked as submitted");
            setTicketPayload(null);
            await load();
            const d = await api.get(`/stock-recon/${selected}`);
            setDetail(d.data);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setMarkingSubmitted(false); }
    };

    const unmarkSubmitted = async () => {
        if (!window.confirm("Clear the submission record for this month?")) return;
        try {
            await api.post(`/stock-recon/${selected}/unmark-submitted`);
            toast.success("Submission cleared");
            await load();
            const d = await api.get(`/stock-recon/${selected}`);
            setDetail(d.data);
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const saveCompany = async (e) => {
        e?.preventDefault();
        try {
            await api.put("/stock-recon/company", companyForm);
            toast.success("Company details saved");
            setCompanyEditing(false);
            await loadCompany();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const totalPeriods = items.length;
    const totalClaimed = items.reduce((a, b) => a + (b.totals?.total || 0), 0);
    const totalSubmitted = items.filter((i) => i.submission).reduce((a, b) => a + (b.totals?.total || 0), 0);
    const totalPending = totalClaimed - totalSubmitted;

    return (
        <div>
            <PageHeader title="Stock reconciliation" subtitle="Upload Takealot's monthly Stock Recon CSV, review claims, and submit the invoice to Takealot Accounts">
                <button data-testid="recon-upload-btn" onClick={() => fileRef.current?.click()} disabled={uploading}
                    className="inline-flex items-center gap-1.5 bg-[color:var(--bl-takealot-blue)] text-white px-3 py-1.5 text-sm rounded-sm hover:brightness-110 disabled:opacity-50">
                    <Upload className="w-4 h-4" /> {uploading ? "Uploading…" : "Upload recon CSV"}
                </button>
                <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={(e) => onUpload(e.target.files?.[0])} data-testid="recon-file-input" />
                <button data-testid="recon-company-btn" onClick={() => setCompanyEditing(true)}
                    className="inline-flex items-center gap-1.5 border border-[color:var(--bl-border)] text-[color:var(--bl-text)] px-3 py-1.5 text-sm rounded-sm hover:bg-[color:var(--bl-surface-2)]">
                    <SettingsIcon className="w-4 h-4" /> Company details
                </button>
            </PageHeader>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <KpiCard label="Recon months" value={formatInt(totalPeriods)} sub="uploaded to date" testid="kpi-recon-months" />
                <KpiCard label="Claimed total" value={formatZAR(totalClaimed)} sub="VAT incl., all months" testid="kpi-recon-claimed" />
                <KpiCard label="Submitted" value={formatZAR(totalSubmitted)} sub={`${items.filter((i) => i.submission).length} months`} accent="green" testid="kpi-recon-submitted" />
                <KpiCard label="Pending" value={formatZAR(totalPending)} sub="not yet submitted" accent="orange" testid="kpi-recon-pending" />
            </div>

            <div className="mb-6 border border-[color:var(--bl-border)] bg-[color:var(--bl-surface)] rounded-sm px-4 py-3 shadow-sm flex flex-wrap items-center justify-between gap-3" data-testid="reminder-panel">
                <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-[color:var(--bl-text-dim)]">Monthly reminder</div>
                    <div className="text-sm text-[color:var(--bl-text)] mt-0.5">
                        Auto-emails all admins on the <strong>first Monday of each month</strong> — but only if the previous month's recon hasn't been uploaded yet.
                    </div>
                    {reminderStatus?.last_sent_at ? (
                        <div className="text-[11px] text-[color:var(--bl-text-dim)] font-mono-num mt-1">
                            Last sent: {reminderStatus.last_sent_at.slice(0, 10)} for period {reminderStatus.last_period} → {reminderStatus.last_sent_to?.join(", ") || "—"}
                        </div>
                    ) : (
                        <div className="text-[11px] text-[color:var(--bl-text-dim)] mt-1">Not sent yet — will fire on the next first Monday.</div>
                    )}
                </div>
                <button data-testid="send-reminder-now" onClick={sendReminderNow} disabled={sendingReminder}
                    className="inline-flex items-center gap-1.5 border border-[color:var(--bl-takealot-orange)] text-[color:var(--bl-takealot-orange)] px-3 py-1.5 text-sm rounded-sm hover:bg-[color:var(--bl-takealot-orange)]/8 disabled:opacity-50 shrink-0">
                    <Mail className="w-4 h-4" /> {sendingReminder ? "Sending…" : "Send test reminder now"}
                </button>
            </div>

            <TakealotAutoFetch existingPeriods={items.map(i => i.period)} />

            <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4">
                {/* Left rail — month list */}
                <div className="border border-[color:var(--bl-border)] bg-[color:var(--bl-surface)] rounded-sm shadow-sm">
                    <div className="px-4 py-3 border-b border-[color:var(--bl-border)] text-[10px] uppercase tracking-[0.18em] text-[color:var(--bl-text-dim)]">Months uploaded</div>
                    <div className="max-h-[600px] overflow-y-auto">
                        {loading && <div className="p-4 text-sm text-[color:var(--bl-text-dim)]">Loading…</div>}
                        {!loading && items.length === 0 && (
                            <div className="p-4 text-sm text-[color:var(--bl-text-dim)]">
                                No recons uploaded yet.<br />
                                <span className="text-[11px]">Download the monthly Stock Reconciliation CSV from your Takealot Seller Portal (Reports → Stock Reconciliation) and upload it here.</span>
                            </div>
                        )}
                        {items.map((i) => (
                            <button key={i.period} onClick={() => setSelected(i.period)} data-testid={`recon-period-${i.period}`}
                                className={`w-full text-left px-4 py-3 border-b border-[color:var(--bl-border)] hover:bg-[color:var(--bl-surface-2)]/60 ${selected === i.period ? "bg-[color:var(--bl-surface-2)]" : ""}`}>
                                <div className="flex justify-between items-baseline">
                                    <div className="font-serif-display text-base">{i.period_label}</div>
                                    <div className="font-mono-num text-xs text-[color:var(--bl-takealot-blue)]">{formatZAR(i.totals?.total || 0)}</div>
                                </div>
                                <div className="mt-1 flex items-center gap-2 text-[10px]">
                                    <span className="text-[color:var(--bl-text-dim)] font-mono-num">{i.claim_line_count} lines</span>
                                    {i.submission ? (
                                        <span className="inline-flex items-center gap-1 text-[color:var(--bl-success)]">
                                            <CheckCircle2 className="w-3 h-3" /> submitted
                                        </span>
                                    ) : (
                                        <span className="inline-flex items-center gap-1 text-[color:var(--bl-warning)]">
                                            <AlertCircle className="w-3 h-3" /> pending
                                        </span>
                                    )}
                                </div>
                            </button>
                        ))}
                    </div>
                </div>

                {/* Detail */}
                <div>
                    {!detail ? (
                        <div className="border border-dashed border-[color:var(--bl-border)] bg-[color:var(--bl-surface)] rounded-sm p-10 text-center text-[color:var(--bl-text-dim)]" data-testid="recon-empty-state">
                            <Package className="w-8 h-8 mx-auto mb-3 opacity-50" />
                            <div className="font-serif-display text-lg text-[color:var(--bl-text)]">Select or upload a month</div>
                            <div className="text-sm mt-1">Grab this month's Stock Reconciliation CSV from the Takealot Seller Portal and click "Upload recon CSV" above.</div>
                        </div>
                    ) : (
                        <ReconDetail detail={detail} onOpenTicket={openTicket} onSubmit={submit} onUnmark={unmarkSubmitted} submitting={submitting} hasEscalationEmail={!!company?.takealot_claims_email} onDelete={() => removeRecon(detail.period)} apiBase={API_BASE} />
                    )}
                </div>
            </div>

            {companyEditing && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setCompanyEditing(false)}>
                    <form onSubmit={saveCompany} onClick={(e) => e.stopPropagation()} data-testid="recon-company-modal"
                        className="w-full max-w-2xl bg-[color:var(--bl-surface)] border border-[color:var(--bl-border)] rounded-sm p-6 shadow-xl max-h-[90vh] overflow-y-auto">
                        <h3 className="font-serif-display text-2xl mb-1">Company details for invoicing</h3>
                        <p className="text-xs text-[color:var(--bl-text-dim)] mb-4">These render as the "Billed from" block on every stock-recon invoice.</p>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {[
                                ["name", "Company name", true],
                                ["reg_no", "Company registration no."],
                                ["vat_no", "VAT number"],
                                ["phone", "Phone"],
                                ["email", "Accounts email"],
                                ["address_line_1", "Address line 1"],
                                ["address_line_2", "Address line 2"],
                                ["address_line_3", "City / postal code"],
                            ].map(([k, l, req]) => (
                                <label key={k} className="block">
                                    <span className="block text-[10px] uppercase tracking-wider text-[color:var(--bl-text-dim)] mb-1">{l}</span>
                                    <input data-testid={`company-${k}`} type={k === "email" ? "email" : "text"} required={req}
                                        value={companyForm[k] || ""} onChange={(e) => setCompanyForm({ ...companyForm, [k]: e.target.value })}
                                        className="w-full bg-[color:var(--bl-bg)] border border-[color:var(--bl-border)] px-3 py-2 text-sm rounded-sm focus:outline-none focus:border-[color:var(--bl-takealot-blue)]" />
                                </label>
                            ))}
                            <label className="block">
                                <span className="block text-[10px] uppercase tracking-wider text-[color:var(--bl-text-dim)] mb-1">Takealot Seller ID</span>
                                <input data-testid="company-seller_id" type="text"
                                    placeholder="e.g. 29892726"
                                    value={companyForm.seller_id || ""}
                                    onChange={(e) => setCompanyForm({ ...companyForm, seller_id: e.target.value })}
                                    className="w-full bg-[color:var(--bl-bg)] border border-[color:var(--bl-border)] px-3 py-2 text-sm rounded-sm focus:outline-none focus:border-[color:var(--bl-takealot-blue)] font-mono-num" />
                                <span className="text-[10px] text-[color:var(--bl-text-mute)] mt-1 block">Auto-populated into every support ticket.</span>
                            </label>
                            <label className="block md:col-span-2">
                                <span className="block text-[10px] uppercase tracking-wider text-[color:var(--bl-text-dim)] mb-1">Takealot support portal URL</span>
                                <input data-testid="company-takealot_support_url" type="url"
                                    value={companyForm.takealot_support_url || ""}
                                    onChange={(e) => setCompanyForm({ ...companyForm, takealot_support_url: e.target.value })}
                                    className="w-full bg-[color:var(--bl-bg)] border border-[color:var(--bl-border)] px-3 py-2 text-sm rounded-sm focus:outline-none focus:border-[color:var(--bl-takealot-blue)]" />
                                <span className="text-[10px] text-[color:var(--bl-text-mute)] mt-1 block">"Prepare support ticket" opens this in a new tab. Default: <span className="font-mono-num">seller.takealot.com/portal/support</span></span>
                            </label>
                            <label className="block">
                                <span className="block text-[10px] uppercase tracking-wider text-[color:var(--bl-text-dim)] mb-1">Ticket category / topic hint</span>
                                <input data-testid="company-takealot_ticket_category" type="text"
                                    value={companyForm.takealot_ticket_category || ""}
                                    onChange={(e) => setCompanyForm({ ...companyForm, takealot_ticket_category: e.target.value })}
                                    className="w-full bg-[color:var(--bl-bg)] border border-[color:var(--bl-border)] px-3 py-2 text-sm rounded-sm focus:outline-none focus:border-[color:var(--bl-takealot-blue)]" />
                            </label>
                            <label className="block">
                                <span className="block text-[10px] uppercase tracking-wider text-[color:var(--bl-text-dim)] mb-1">Optional escalation email <span className="text-[color:var(--bl-text-mute)] normal-case">(only if Takealot has given you one)</span></span>
                                <input data-testid="company-takealot_claims_email" type="email"
                                    placeholder="leave blank if unsure"
                                    value={companyForm.takealot_claims_email || ""}
                                    onChange={(e) => setCompanyForm({ ...companyForm, takealot_claims_email: e.target.value })}
                                    className="w-full bg-[color:var(--bl-bg)] border border-[color:var(--bl-border)] px-3 py-2 text-sm rounded-sm focus:outline-none focus:border-[color:var(--bl-takealot-blue)]" />
                            </label>
                        </div>

                        <div className="flex justify-end gap-2 mt-6">
                            <button type="button" onClick={() => setCompanyEditing(false)} className="px-3 py-1.5 text-sm border border-[color:var(--bl-border)] rounded-sm">Cancel</button>
                            <button data-testid="save-company" type="submit" className="px-3 py-1.5 text-sm bg-[color:var(--bl-takealot-blue)] text-white rounded-sm hover:brightness-110">Save</button>
                        </div>
                    </form>
                </div>
            )}
            {ticketPayload && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setTicketPayload(null)}>
                    <div onClick={(e) => e.stopPropagation()} data-testid="ticket-modal"
                        className="w-full max-w-3xl bg-[color:var(--bl-surface)] border border-[color:var(--bl-border)] rounded-sm p-6 shadow-xl max-h-[92vh] overflow-y-auto">
                        <div className="flex items-start justify-between gap-3 mb-4">
                            <div>
                                <h3 className="font-serif-display text-2xl">Log a Takealot support ticket</h3>
                                <p className="text-xs text-[color:var(--bl-text-dim)] mt-1">The deep-link opens Takealot's ticket form with department + query type already selected. Paste each remaining field one-by-one — the "copy" buttons put each value on your clipboard.</p>
                            </div>
                            <button onClick={() => setTicketPayload(null)} className="text-[color:var(--bl-text-dim)] hover:text-[color:var(--bl-text)]" aria-label="Close"><Trash2 className="w-4 h-4" /></button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-5">
                            <a href={ticketPayload.support_url} target="_blank" rel="noreferrer" data-testid="ticket-open-portal"
                                className="inline-flex items-center justify-center gap-1.5 bg-[color:var(--bl-takealot-blue)] text-white px-3 py-2.5 rounded-sm hover:brightness-110">
                                <ExternalLink className="w-4 h-4" /> Open Takealot ticket form
                            </a>
                            <div className="flex gap-2">
                                <a href={`${API_BASE}/stock-recon/${detail.period}/invoice.pdf`} target="_blank" rel="noreferrer" data-testid="ticket-download-pdf"
                                    className="flex-1 inline-flex items-center justify-center gap-1.5 border border-[color:var(--bl-border)] px-3 py-2.5 rounded-sm hover:bg-[color:var(--bl-surface-2)]">
                                    <Download className="w-4 h-4" /> Invoice PDF
                                </a>
                                <a href={`${API_BASE}/stock-recon/${detail.period}/invoice.csv`} target="_blank" rel="noreferrer" data-testid="ticket-download-csv"
                                    className="flex-1 inline-flex items-center justify-center gap-1.5 border border-[color:var(--bl-border)] px-3 py-2.5 rounded-sm hover:bg-[color:var(--bl-surface-2)]">
                                    <FileText className="w-4 h-4" /> Original CSV
                                </a>
                            </div>
                        </div>

                        <div className="text-[10px] uppercase tracking-[0.18em] text-[color:var(--bl-text-dim)] mb-2">Form fields to paste — top to bottom</div>
                        <div className="space-y-3">
                            {(ticketPayload.form_fields || []).map((f) => (
                                <div key={f.copy_id} data-testid={`ticket-field-${f.copy_id}`}
                                    className="border border-[color:var(--bl-border)] bg-[color:var(--bl-bg)]/40 rounded-sm p-3">
                                    <div className="flex items-center justify-between mb-1.5 gap-2">
                                        <div>
                                            <span className="text-xs font-medium text-[color:var(--bl-text)]">{f.label}</span>
                                            {f.note && <span className="ml-2 text-[10px] text-[color:var(--bl-text-mute)] italic">{f.note}</span>}
                                        </div>
                                        {f.value && (
                                            <button onClick={() => copyToClipboard(f.value, f.label)} data-testid={`copy-${f.copy_id}`}
                                                className="inline-flex items-center gap-1 text-[11px] text-[color:var(--bl-takealot-blue)] hover:underline shrink-0">
                                                <Copy className="w-3 h-3" /> copy
                                            </button>
                                        )}
                                    </div>
                                    {f.textarea ? (
                                        <textarea readOnly value={f.value} rows={7}
                                            className="w-full bg-[color:var(--bl-bg)] border border-[color:var(--bl-border)] rounded-sm px-2.5 py-2 text-[11px] font-mono-num resize-y" />
                                    ) : (
                                        <div className="bg-[color:var(--bl-bg)] border border-[color:var(--bl-border)] rounded-sm px-2.5 py-2 text-xs font-mono-num break-all">
                                            {f.value || <span className="text-[color:var(--bl-text-mute)] italic">— not set —</span>}
                                        </div>
                                    )}
                                </div>
                            ))}
                            <div className="border border-[color:var(--bl-border)] bg-[color:var(--bl-bg)]/40 rounded-sm p-3">
                                <div className="text-xs font-medium mb-1.5">Attachments</div>
                                <div className="text-[11px] text-[color:var(--bl-text-dim)]">Drag the downloaded PDF + CSV into the "Add file" area on the Takealot form.</div>
                            </div>
                        </div>

                        <div className="mt-6 pt-4 border-t border-[color:var(--bl-border)]">
                            <div className="text-[10px] uppercase tracking-[0.18em] text-[color:var(--bl-text-dim)] mb-2">Once submitted · paste Takealot's ticket reference back here</div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2">
                                <input placeholder="e.g. #12345678 or TT-2026-078123" value={ticketRef} onChange={(e) => setTicketRef(e.target.value)} data-testid="ticket-reference-input"
                                    className="bg-[color:var(--bl-bg)] border border-[color:var(--bl-border)] px-3 py-2 text-sm rounded-sm focus:outline-none focus:border-[color:var(--bl-takealot-blue)]" />
                                <input placeholder="notes (optional)" value={ticketNotes} onChange={(e) => setTicketNotes(e.target.value)} data-testid="ticket-notes-input"
                                    className="bg-[color:var(--bl-bg)] border border-[color:var(--bl-border)] px-3 py-2 text-sm rounded-sm focus:outline-none focus:border-[color:var(--bl-takealot-blue)]" />
                            </div>
                            <button onClick={markSubmitted} disabled={markingSubmitted} data-testid="ticket-mark-submitted"
                                className="inline-flex items-center gap-1.5 bg-[color:var(--bl-success)] text-white px-3 py-1.5 rounded-sm hover:brightness-110 disabled:opacity-50">
                                <CheckCircle2 className="w-4 h-4" /> {markingSubmitted ? "Saving…" : "Mark as submitted"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function ReconDetail({ detail, onOpenTicket, onSubmit, onUnmark, submitting, hasEscalationEmail, onDelete, apiBase }) {
    const t = detail.totals || {};
    const lines = detail.claim_lines || [];
    const rows = detail.rows || [];
    // Non-claim outflow summary
    const outflow = rows.reduce((acc, r) => ({
        shipped: acc.shipped + (r.shipped_to_customer || 0),
        removed_disposed: acc.removed_disposed + (r.removal_orders_disposed || 0),
        other_out: acc.other_out + (r.other_outflow || 0),
        down: acc.down + (r.warehouse_down_adjustments || 0),
    }), { shipped: 0, removed_disposed: 0, other_out: 0, down: 0 });

    return (
        <div data-testid="recon-detail">
            <div className="border border-[color:var(--bl-border)] bg-[color:var(--bl-surface)] rounded-sm p-4 mb-4 shadow-sm">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                        <div className="font-serif-display text-2xl">{detail.period_label}</div>
                        <div className="text-xs text-[color:var(--bl-text-dim)] font-mono-num mt-1">
                            Invoice {detail.invoice_number} · uploaded {(detail.uploaded_at || "").slice(0, 10)} by {detail.uploaded_by}
                        </div>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                        <a href={`${apiBase}/stock-recon/${detail.period}/invoice.pdf`} target="_blank" rel="noreferrer"
                            data-testid="download-invoice-pdf"
                            className="inline-flex items-center gap-1.5 border border-[color:var(--bl-border)] text-[color:var(--bl-text)] px-3 py-1.5 text-sm rounded-sm hover:bg-[color:var(--bl-surface-2)]">
                            <Download className="w-4 h-4" /> Invoice PDF
                        </a>
                        <a href={`${apiBase}/stock-recon/${detail.period}/invoice.csv`} target="_blank" rel="noreferrer"
                            data-testid="download-csv"
                            className="inline-flex items-center gap-1.5 border border-[color:var(--bl-border)] text-[color:var(--bl-text)] px-3 py-1.5 text-sm rounded-sm hover:bg-[color:var(--bl-surface-2)]">
                            <FileText className="w-4 h-4" /> Original CSV
                        </a>
                        <button data-testid="prepare-ticket" onClick={onOpenTicket} disabled={detail.totals.total <= 0}
                            className="inline-flex items-center gap-1.5 bg-[color:var(--bl-takealot-orange)] text-white px-3 py-1.5 text-sm rounded-sm hover:brightness-110 disabled:opacity-50">
                            <Ticket className="w-4 h-4" /> Prepare support ticket
                        </button>
                        {hasEscalationEmail && (
                            <button data-testid="email-fallback" onClick={onSubmit} disabled={submitting || detail.totals.total <= 0}
                                title="Optional: email the invoice via Resend to your Takealot escalation address"
                                className="inline-flex items-center gap-1.5 border border-[color:var(--bl-border)] text-[color:var(--bl-text-dim)] px-3 py-1.5 text-sm rounded-sm hover:bg-[color:var(--bl-surface-2)] disabled:opacity-50">
                                <Send className="w-4 h-4" /> {submitting ? "Sending…" : "Email fallback"}
                            </button>
                        )}
                        {detail.submission && (
                            <button data-testid="unmark-submitted" onClick={onUnmark}
                                className="inline-flex items-center gap-1 text-xs text-[color:var(--bl-text-dim)] hover:text-[color:var(--bl-text)] px-2">
                                <Undo2 className="w-3 h-3" /> Clear submission
                            </button>
                        )}
                        <button data-testid="delete-recon" onClick={onDelete}
                            className="inline-flex items-center gap-1 text-xs text-[color:var(--bl-danger)] hover:underline px-2">
                            <Trash2 className="w-3 h-3" /> Delete
                        </button>
                    </div>
                </div>

                {detail.submission && (
                    <div className="mt-3 p-3 bg-[color:var(--bl-success)]/8 border border-[color:var(--bl-success)]/30 rounded-sm text-xs text-[color:var(--bl-text)] flex items-start gap-2" data-testid="submission-status">
                        <CheckCircle2 className="w-4 h-4 text-[color:var(--bl-success)] shrink-0 mt-0.5" />
                        <div className="min-w-0 flex-1">
                            <div className="font-medium">
                                {detail.submission.channel === "support_ticket" ? "Ticket logged" : detail.submission.channel === "email" ? "Emailed" : "Submitted"}
                                {detail.submission.ticket_reference ? <span className="ml-2 font-mono-num">#{detail.submission.ticket_reference}</span> : detail.submission.recipient ? <span className="ml-2 font-mono-num">→ {detail.submission.recipient}</span> : null}
                            </div>
                            <div className="text-[color:var(--bl-text-dim)] font-mono-num">
                                {(detail.submission.submitted_at || "").slice(0, 19).replace("T", " ")} · {formatZAR(detail.submission.total)}
                                {detail.submission.notes ? <span className="ml-2">· {detail.submission.notes}</span> : null}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <KpiCard label="Subtotal (ex VAT)" value={formatZAR(t.subtotal || 0)} testid="detail-subtotal" />
                <KpiCard label="VAT (15%)" value={formatZAR(t.vat || 0)} accent="orange" testid="detail-vat" />
                <KpiCard label="Total claim" value={formatZAR(t.total || 0)} accent="green" sub={`${lines.length} SKUs`} testid="detail-total" />
                <KpiCard label="Units lost" value={formatInt(outflow.down)} sub={`${outflow.shipped} shipped this month`} testid="detail-units-lost" />
            </div>

            {lines.length > 0 && (
                <div className="border border-[color:var(--bl-border)] bg-[color:var(--bl-surface)] rounded-sm shadow-sm mb-4 overflow-hidden">
                    <div className="px-4 py-3 border-b border-[color:var(--bl-border)] flex justify-between">
                        <div className="font-serif-display text-lg">Invoice line items</div>
                        <div className="text-xs text-[color:var(--bl-text-dim)]">Only SKUs with warehouse_down_adjustments {'>'} 0 appear here</div>
                    </div>
                    <table className="w-full text-sm">
                        <thead className="text-[10px] uppercase tracking-[0.18em] text-[color:var(--bl-text-dim)] border-b border-[color:var(--bl-border)] bg-[color:var(--bl-bg-2)]/40">
                            <tr>
                                <th className="text-left px-4 py-2">Product</th>
                                <th className="text-right px-3 py-2">Qty</th>
                                <th className="text-right px-3 py-2">Rate</th>
                                <th className="text-right px-3 py-2">VAT</th>
                                <th className="text-right px-3 py-2">Total</th>
                            </tr>
                        </thead>
                        <tbody>
                            {lines.map((l) => (
                                <tr key={l.sku} className="border-b border-[color:var(--bl-border)]" data-testid={`recon-line-${l.sku}`}>
                                    <td className="px-4 py-3">
                                        <div className="text-[color:var(--bl-text)]">{l.product_title}</div>
                                        <div className="text-[10px] text-[color:var(--bl-text-dim)] font-mono-num mt-0.5">TSIN {l.tsin_id} · SKU {l.sku}</div>
                                    </td>
                                    <td className="px-3 py-3 text-right font-mono-num">{l.qty}</td>
                                    <td className="px-3 py-3 text-right font-mono-num">{formatZAR(l.rate)}</td>
                                    <td className="px-3 py-3 text-right font-mono-num text-[color:var(--bl-takealot-orange)]">{formatZAR(l.vat)}</td>
                                    <td className="px-3 py-3 text-right font-mono-num font-medium">{formatZAR(l.total)}</td>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot className="bg-[color:var(--bl-bg-2)]/60 border-t-2 border-[color:var(--bl-border-strong)] font-mono-num">
                            <tr><td colSpan={4} className="px-4 py-2 text-right text-xs uppercase tracking-wider text-[color:var(--bl-text-dim)]">Subtotal (ex VAT)</td><td className="px-3 py-2 text-right">{formatZAR(t.subtotal)}</td></tr>
                            <tr><td colSpan={4} className="px-4 py-2 text-right text-xs uppercase tracking-wider text-[color:var(--bl-text-dim)]">VAT 15%</td><td className="px-3 py-2 text-right text-[color:var(--bl-takealot-orange)]">{formatZAR(t.vat)}</td></tr>
                            <tr><td colSpan={4} className="px-4 py-2 text-right text-sm font-medium">Total (VAT incl.)</td><td className="px-3 py-2 text-right text-[color:var(--bl-success)] font-medium">{formatZAR(t.total)}</td></tr>
                        </tfoot>
                    </table>
                </div>
            )}

            <details className="border border-[color:var(--bl-border)] bg-[color:var(--bl-surface)] rounded-sm shadow-sm">
                <summary className="cursor-pointer px-4 py-3 border-b border-[color:var(--bl-border)] text-sm font-medium">
                    Full recon ledger ({rows.length} SKUs) — opening / inflow / outflow / closing
                </summary>
                <div className="overflow-x-auto">
                    <table className="w-full text-xs min-w-[900px]">
                        <thead className="text-[10px] uppercase tracking-[0.18em] text-[color:var(--bl-text-dim)] bg-[color:var(--bl-bg-2)]/40">
                            <tr>
                                <th className="text-left px-3 py-2">SKU / TSIN</th>
                                <th className="text-right px-2 py-2">Open</th>
                                <th className="text-right px-2 py-2">Shipped in</th>
                                <th className="text-right px-2 py-2">Returns in</th>
                                <th className="text-right px-2 py-2">Total in</th>
                                <th className="text-right px-2 py-2">Sold</th>
                                <th className="text-right px-2 py-2">Down adj</th>
                                <th className="text-right px-2 py-2">Total out</th>
                                <th className="text-right px-2 py-2">Close</th>
                                <th className="text-right px-2 py-2">Claim</th>
                            </tr>
                        </thead>
                        <tbody className="font-mono-num">
                            {rows.map((r, i) => (
                                <tr key={r.sku || i} className="border-b border-[color:var(--bl-border)]">
                                    <td className="px-3 py-2">
                                        <div className="text-[color:var(--bl-text)]">{r.sku}</div>
                                        <div className="text-[9px] text-[color:var(--bl-text-mute)]">{r.tsin_id}</div>
                                    </td>
                                    <td className="px-2 py-2 text-right">{formatInt(r.opening_balance)}</td>
                                    <td className="px-2 py-2 text-right">{formatInt(r.shipment_received)}</td>
                                    <td className="px-2 py-2 text-right">{formatInt(r.returns_received_into_goodstock)}</td>
                                    <td className="px-2 py-2 text-right">{formatInt(r.total_inflow)}</td>
                                    <td className="px-2 py-2 text-right">{formatInt(r.shipped_to_customer)}</td>
                                    <td className={`px-2 py-2 text-right ${r.warehouse_down_adjustments > 0 ? "text-[color:var(--bl-danger)] font-medium" : ""}`}>{formatInt(r.warehouse_down_adjustments)}</td>
                                    <td className="px-2 py-2 text-right">{formatInt(r.total_outflow)}</td>
                                    <td className="px-2 py-2 text-right">{formatInt(r.closing_balance)}</td>
                                    <td className={`px-2 py-2 text-right ${r.total_claim_value > 0 ? "text-[color:var(--bl-success)]" : "text-[color:var(--bl-text-mute)]"}`}>{formatZAR(r.total_claim_value || 0)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </details>
        </div>
    );
}

function TakealotAutoFetch({ existingPeriods = [] }) {
    const [cfg, setCfg] = useState(null);
    const [form, setForm] = useState({ jwt: "", fingerprint: "" });
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [fetching, setFetching] = useState(false);
    const [togglingAuto, setTogglingAuto] = useState(false);
    const [runningAuto, setRunningAuto] = useState(false);
    const [fetchPeriod, setFetchPeriod] = useState("");
    const [fetchError, setFetchError] = useState(null);

    const periodExists = fetchPeriod && /^\d{6}$/.test(fetchPeriod) && existingPeriods.includes(fetchPeriod);

    const load = () => api.get("/takealot-reports/config").then((r) => {
        setCfg(r.data);
        if (r.data?.fingerprint) setForm((f) => ({ jwt: f.jwt, fingerprint: r.data.fingerprint }));
    }).catch(() => {});
    useEffect(() => { load(); }, []);

    const save = async (e) => {
        e?.preventDefault();
        if (!form.jwt || !form.fingerprint) { toast.error("Paste both JWT and fingerprint from your browser DevTools."); return; }
        setSaving(true);
        try {
            const jwt = form.jwt.replace(/^Bearer\s+/i, "").trim();
            await api.put("/takealot-reports/config", { jwt, fingerprint: form.fingerprint.trim() });
            toast.success("Credentials saved");
            setForm({ jwt: "", fingerprint: form.fingerprint });
            await load();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setSaving(false); }
    };
    const test = async () => {
        setTesting(true);
        try { await api.post("/takealot-reports/test"); toast.success("Connection OK — Takealot accepted the JWT"); }
        catch (err) { toast.error(formatApiError(err)); }
        finally { setTesting(false); }
    };
    const fetchNow = async () => {
        setFetchError(null);
        if (!/^\d{6}$/.test(fetchPeriod)) {
            const msg = "Period must be YYYYMM (e.g. 202607)";
            setFetchError(msg);
            toast.error(msg);
            return;
        }
        if (existingPeriods.includes(fetchPeriod)) {
            const msg = `${fmtPeriod(fetchPeriod)} is already uploaded. Delete it from the months list first if you want to re-fetch.`;
            setFetchError(msg);
            toast.error(msg);
            return;
        }
        if (!window.confirm(`Fetch Takealot Stock Recon for ${fetchPeriod}? Takes 60-90 seconds while Takealot generates the report.`)) return;
        setFetching(true);
        try {
            const r = await api.post(`/takealot-reports/fetch/${fetchPeriod}`);
            toast.success(`Fetched ${r.data.period_label} — ${r.data.claim_line_count} claim lines`);
            setFetchPeriod("");
            window.location.reload();
        } catch (err) {
            const msg = formatApiError(err);
            setFetchError(msg);
            toast.error(msg);
        }
        finally { setFetching(false); }
    };
    const toggleAuto = async () => {
        setTogglingAuto(true);
        try {
            await api.patch("/takealot-reports/auto-fetch", { enabled: !cfg?.auto_fetch_enabled });
            toast.success(`Auto-fetch ${!cfg?.auto_fetch_enabled ? "enabled" : "disabled"}`);
            await load();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setTogglingAuto(false); }
    };
    const runAutoNow = async () => {
        if (!window.confirm("Run the monthly auto-fetch now? It'll grab last month's Stock Recon if you don't already have it (60-90s).")) return;
        setRunningAuto(true);
        setFetchError(null);
        try {
            const r = await api.post("/takealot-reports/auto-fetch/run-now");
            if (r.data?.skipped) {
                const period = r.data.period ? ` (${fmtPeriod(r.data.period)})` : "";
                const msg = `Auto-fetch skipped — ${r.data.reason}${period}. Delete the existing month if you want to re-fetch it.`;
                setFetchError(msg);
                toast.warning(msg);
            } else if (r.data?.error) {
                const msg = `Auto-fetch failed — ${r.data.error}`;
                setFetchError(msg);
                toast.error(msg);
            } else {
                toast.success(`Auto-fetch ran for ${r.data.period}`);
            }
            await load();
            if (r.data?.ok) setTimeout(() => window.location.reload(), 800);
        } catch (err) {
            const msg = formatApiError(err);
            setFetchError(msg);
            toast.error(msg);
        }
        finally { setRunningAuto(false); }
    };

    const fmtDT = (iso) => iso ? iso.slice(0, 16).replace("T", " ") : "—";
    const fmtNext = (iso) => {
        if (!iso) return "—";
        try {
            const d = new Date(iso);
            return d.toLocaleString("en-ZA", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone: "Africa/Johannesburg" }) + " SAST";
        } catch { return iso; }
    };

    return (
        <div className="mb-6 border border-[color:var(--bl-border)] bg-[color:var(--bl-surface)] rounded-sm p-4 shadow-sm" data-testid="takealot-autofetch">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-[color:var(--bl-text-dim)]">Takealot auto-fetch</div>
                    <div className="text-sm">Paste your Seller Portal JWT + fingerprint to pull the Stock Recon CSV directly. Hard-capped at one request per month, aborts on 401/403/429.</div>
                </div>
                <div className="text-xs">
                    {cfg?.configured ? (
                        <span className="inline-flex items-center gap-1.5 text-[color:var(--bl-success)] font-mono-num">
                            <CheckCircle2 className="w-4 h-4" /> Configured
                        </span>
                    ) : (
                        <span className="inline-flex items-center gap-1.5 text-[color:var(--bl-text-dim)]">
                            <AlertCircle className="w-4 h-4" /> Not configured
                        </span>
                    )}
                </div>
            </div>

            {cfg?.configured && (
                <div className="mb-3 grid grid-cols-1 md:grid-cols-4 gap-2 border border-[color:var(--bl-border)] bg-[color:var(--bl-bg)] rounded-sm p-3" data-testid="autofetch-status">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.18em] text-[color:var(--bl-text-dim)]">Scheduler</div>
                        <div className="text-sm font-mono-num flex items-center gap-2 mt-0.5">
                            <span className={`inline-block w-2 h-2 rounded-full ${cfg.auto_fetch_enabled ? "bg-[color:var(--bl-success)]" : "bg-[color:var(--bl-text-dim)]"}`} />
                            <span>{cfg.auto_fetch_enabled ? "Enabled" : "Disabled"}</span>
                        </div>
                        <button type="button" onClick={toggleAuto} disabled={togglingAuto} data-testid="autofetch-toggle"
                            className="mt-1.5 text-[11px] underline text-[color:var(--bl-takealot-blue)] hover:brightness-125 disabled:opacity-50 block">
                            {togglingAuto ? "…" : (cfg.auto_fetch_enabled ? "Disable" : "Enable")}
                        </button>
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.18em] text-[color:var(--bl-text-dim)]">Next auto-run</div>
                        <div className="text-sm font-mono-num mt-0.5" data-testid="autofetch-next">{fmtNext(cfg.next_auto_fetch_at)}</div>
                        <div className="text-[10px] text-[color:var(--bl-text-mute)] mt-0.5">Fires on the 5th of each month</div>
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.18em] text-[color:var(--bl-text-dim)]">Last auto-run</div>
                        <div className="text-sm font-mono-num mt-0.5" data-testid="autofetch-last">{fmtDT(cfg.last_auto_fetch_at)}</div>
                        {cfg.last_auto_fetch_period && (
                            <div className="text-[10px] text-[color:var(--bl-text-mute)] mt-0.5">Period: {cfg.last_auto_fetch_period}</div>
                        )}
                        {cfg.last_auto_fetch_status && (
                            <div className={`text-[10px] mt-0.5 ${cfg.last_auto_fetch_status.startsWith("error") ? "text-[color:var(--bl-danger)]" : "text-[color:var(--bl-success)]"}`}>
                                {cfg.last_auto_fetch_status}
                            </div>
                        )}
                    </div>
                    <div className="flex items-end justify-start md:justify-end">
                        <button type="button" onClick={runAutoNow} disabled={runningAuto || !cfg.auto_fetch_enabled} data-testid="autofetch-run-now"
                            className="border border-[color:var(--bl-takealot-blue)] text-[color:var(--bl-takealot-blue)] px-3 py-1.5 text-sm rounded-sm hover:bg-[color:var(--bl-takealot-blue)] hover:text-white transition disabled:opacity-50">
                            {runningAuto ? "Running…" : "Run auto-fetch now"}
                        </button>
                    </div>
                </div>
            )}

            {cfg?.last_success_at && (
                <div className="text-[11px] text-[color:var(--bl-success)] font-mono-num mb-2">Last successful fetch: {cfg.last_success_at.slice(0, 19).replace("T", " ")}</div>
            )}
            {cfg?.last_error && (
                <div className="text-[11px] text-[color:var(--bl-danger)] font-mono-num mb-2">Last error: {cfg.last_error}</div>
            )}

            {fetchError && (
                <div className="text-[11px] text-[color:var(--bl-danger)] font-mono-num mb-2 border border-[color:var(--bl-danger)]/40 bg-[color:var(--bl-danger)]/5 rounded-sm px-2 py-1.5 flex items-start gap-2" data-testid="fetch-error-banner">
                    <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-none" />
                    <span className="flex-1">{fetchError}</span>
                    <button type="button" onClick={() => setFetchError(null)} className="text-[color:var(--bl-text-dim)] hover:text-[color:var(--bl-text)] text-[10px] uppercase tracking-wider">dismiss</button>
                </div>
            )}

            <form onSubmit={save} className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-3">
                <input data-testid="tk-jwt" type="password" placeholder={cfg?.configured ? "paste to replace…" : "Bearer JWT"} value={form.jwt} onChange={(e) => setForm({ ...form, jwt: e.target.value })}
                    className="md:col-span-2 bg-[color:var(--bl-bg)] border border-[color:var(--bl-border)] px-3 py-2 text-sm rounded-sm focus:outline-none focus:border-[color:var(--bl-takealot-blue)] font-mono-num" />
                <input data-testid="tk-fingerprint" type="text" placeholder="fingerprint" value={form.fingerprint} onChange={(e) => setForm({ ...form, fingerprint: e.target.value })}
                    className="bg-[color:var(--bl-bg)] border border-[color:var(--bl-border)] px-3 py-2 text-sm rounded-sm focus:outline-none focus:border-[color:var(--bl-takealot-blue)] font-mono-num" />
                <div className="md:col-span-3 flex gap-2 flex-wrap">
                    <button type="submit" disabled={saving} data-testid="tk-save"
                        className="bg-[color:var(--bl-takealot-blue)] text-white px-3 py-1.5 text-sm rounded-sm hover:brightness-110 disabled:opacity-50">
                        {saving ? "Saving…" : "Save credentials"}
                    </button>
                    <button type="button" onClick={test} disabled={testing || !cfg?.configured} data-testid="tk-test"
                        className="border border-[color:var(--bl-border)] px-3 py-1.5 text-sm rounded-sm hover:bg-[color:var(--bl-surface-2)] disabled:opacity-50">
                        {testing ? "Testing…" : "Test connection"}
                    </button>
                    <div className="flex-1" />
                    <div className="flex flex-col items-end">
                        <div className="flex gap-2">
                            <input placeholder="YYYYMM" value={fetchPeriod} onChange={(e) => { setFetchPeriod(e.target.value); setFetchError(null); }} data-testid="tk-period"
                                className={`w-32 bg-[color:var(--bl-bg)] border px-3 py-1.5 text-sm rounded-sm focus:outline-none font-mono-num ${periodExists ? "border-[color:var(--bl-danger)] focus:border-[color:var(--bl-danger)]" : "border-[color:var(--bl-border)] focus:border-[color:var(--bl-takealot-blue)]"}`} />
                            <button type="button" onClick={fetchNow} disabled={fetching || !cfg?.configured || !fetchPeriod || periodExists} data-testid="tk-fetch"
                                className="bg-[color:var(--bl-takealot-orange)] text-white px-3 py-1.5 text-sm rounded-sm hover:brightness-110 disabled:opacity-50 inline-flex items-center gap-1.5">
                                <Download className="w-4 h-4" /> {fetching ? "Fetching…" : "Fetch now"}
                            </button>
                        </div>
                        {periodExists && (
                            <div className="text-[10px] text-[color:var(--bl-danger)] mt-1" data-testid="period-exists-hint">
                                {fmtPeriod(fetchPeriod)} already uploaded — delete it first to re-fetch
                            </div>
                        )}
                    </div>
                </div>
            </form>
            <div className="text-[10px] text-[color:var(--bl-text-mute)] leading-relaxed">
                Getting these: open sellers.takealot.com → F12 → Network → refresh → click any seller-api.takealot.com request → copy the <code>authorization: Bearer ...</code> value and the <code>fingerprint</code> header. JWTs expire — Test Connection will 400 when they do, then re-paste.
            </div>
        </div>
    );
}
